import type { Expense } from '../types';

export function formatCurrency(amount: number, currency = 'INR') {
  const locale = currency === 'INR' ? 'en-IN' : 'en-US';
  try {
    return new Intl.NumberFormat(locale, { style: 'currency', currency, maximumFractionDigits: 0 }).format(amount);
  } catch {
    return `${currency} ${Math.round(amount).toLocaleString()}`;
  }
}

export function currencySymbol(currency: string) {
  return ({ INR: '₹', USD: '$', EUR: '€', GBP: '£' } as Record<string, string>)[currency] || currency;
}

export function formatDateTime(value: string) {
  const date = new Date(value);
  return Number.isNaN(date.getTime()) ? 'Unknown date' : new Intl.DateTimeFormat('en-IN', { day: 'numeric', month: 'short', hour: 'numeric', minute: '2-digit' }).format(date);
}

export function formatDate(value: string) {
  const date = new Date(value);
  return Number.isNaN(date.getTime()) ? 'Unknown date' : new Intl.DateTimeFormat('en-IN', { day: 'numeric', month: 'short', year: 'numeric' }).format(date);
}

export function formatMonth(value = new Date()) {
  return new Intl.DateTimeFormat('en-IN', { month: 'long', year: 'numeric' }).format(value);
}

export function toDateTimeInputValue(value: string) {
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return '';
  const pad = (number: number) => String(number).padStart(2, '0');
  return `${date.getFullYear()}-${pad(date.getMonth() + 1)}-${pad(date.getDate())}T${pad(date.getHours())}:${pad(date.getMinutes())}`;
}

export function fromDateTimeInputValue(value: string) {
  const date = new Date(value);
  return Number.isNaN(date.getTime()) ? new Date().toISOString() : date.toISOString();
}

export function isToday(value: string) {
  const date = new Date(value);
  const today = new Date();
  return date.getFullYear() === today.getFullYear() && date.getMonth() === today.getMonth() && date.getDate() === today.getDate();
}

export function isCurrentMonth(value: string) {
  const date = new Date(value);
  const today = new Date();
  return date.getFullYear() === today.getFullYear() && date.getMonth() === today.getMonth();
}

export function isCurrentWeek(value: string) {
  const date = new Date(value);
  const now = new Date();
  const start = new Date(now);
  const day = start.getDay() || 7;
  start.setDate(start.getDate() - day + 1);
  start.setHours(0, 0, 0, 0);
  return date >= start && date <= now;
}

export function sumExpenses(expenses: Expense[]) {
  return expenses.reduce((sum, expense) => sum + (expense.kind === 'inflow' ? 0 : expense.amount), 0);
}

export function sumInflows(expenses: Expense[]) {
  return expenses.reduce((sum, expense) => sum + (expense.kind === 'inflow' ? expense.amount : 0), 0);
}

export function netBalance(expenses: Expense[]) {
  return sumInflows(expenses) - sumExpenses(expenses);
}

export function getCategoryTotals(expenses: Expense[]) {
  return expenses.reduce<Record<string, number>>((totals, expense) => {
    if (expense.kind === 'outflow') totals[expense.category] = (totals[expense.category] || 0) + expense.amount;
    return totals;
  }, {});
}

export function getDailyTotals(expenses: Expense[]) {
  const totals = Array.from({ length: 7 }, (_, index) => ({ label: '', value: 0, index }));
  const today = new Date();
  const labels = ['Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat', 'Sun'];
  for (let i = 0; i < 7; i += 1) {
    const date = new Date(today);
    date.setDate(today.getDate() - (6 - i));
    totals[i].label = labels[(date.getDay() + 6) % 7];
    totals[i].value = expenses.filter((expense) => {
      const expenseDate = new Date(expense.occurredAt);
      return expenseDate.getFullYear() === date.getFullYear() && expenseDate.getMonth() === date.getMonth() && expenseDate.getDate() === date.getDate();
      }).reduce((sum, expense) => sum + (expense.kind === 'outflow' ? expense.amount : 0), 0);
  }
  return totals;
}
